import { motion } from "framer-motion";
import { Bot, FlaskConical, Swords, ArrowRight, Check, type LucideIcon } from "lucide-react";
import { PRODUCTS, type Product } from "@/data/content";
import { SectionHeading } from "@/components/SectionHeading";
import { cn } from "@/lib/utils";

const ICONS: Record<string, LucideIcon> = {
  carcin: Bot,
  crucible: FlaskConical,
  narsil: Swords,
};

const ACCENT = {
  signal: {
    text: "text-signal-400",
    bg: "bg-signal-500/10",
    border: "border-signal-500/30",
    dot: "text-signal-500",
  },
  invert: {
    text: "text-invert-400",
    bg: "bg-invert-500/10",
    border: "border-invert-500/30",
    dot: "text-invert-400",
  },
} as const;

const FLOW = [
  { stage: "Forge", name: "Crucible", desc: "agents build & review" },
  { stage: "Deliver", name: "Narsil", desc: "agents ship, safely" },
  { stage: "Operate", name: "Carcin", desc: "the product in the field" },
];

function FlowStrip() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.6 }}
      className="mt-12 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
    >
      {FLOW.map((f, i) => (
        <div key={f.name} className="flex flex-1 items-center gap-3">
          <div className="flex-1 panel rounded-[var(--radius-md)] px-5 py-4">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-signal-500">{f.stage}</div>
            <div className="mt-1 font-display text-base font-semibold text-ink-50">{f.name}</div>
            <div className="font-mono text-[0.62rem] uppercase tracking-[0.12em] text-ink-500">{f.desc}</div>
          </div>
          {i < FLOW.length - 1 && (
            <ArrowRight className="hidden size-5 shrink-0 text-ink-600 sm:block" />
          )}
        </div>
      ))}
    </motion.div>
  );
}

function ProductCard({ p, i }: { p: Product; i: number }) {
  const a = ACCENT[p.accent];
  const Icon = ICONS[p.id];
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="panel relative overflow-hidden rounded-[var(--radius-lg)] p-7 lg:p-9"
    >
      {/* accent rail */}
      <div className={cn("absolute inset-y-0 left-0 w-px", a.bg)} />

      <div className="flex items-center gap-4">
        <span className={cn("font-mono text-sm", a.dot)}>{p.glyph}</span>
        <div className={cn("inline-flex size-11 items-center justify-center rounded-[var(--radius-md)] border", a.bg, a.border, a.text)}>
          <Icon className="size-5" />
        </div>
        <div>
          <h3 className="font-display text-2xl font-bold tracking-tight text-ink-50">{p.name}</h3>
          <p className={cn("font-mono text-[0.7rem] uppercase tracking-[0.14em]", a.text)}>{p.tagline}</p>
        </div>
      </div>

      <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-100">{p.summary}</p>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <p className="text-sm leading-relaxed text-ink-400">{p.body}</p>
        <ul className="flex flex-col gap-3">
          {p.points.map((pt) => (
            <li key={pt} className="flex items-start gap-2.5 text-sm text-ink-300">
              <Check className={cn("mt-0.5 size-4 shrink-0", a.text)} />
              {pt}
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export function Products() {
  return (
    <section id="products" className="relative px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          index="03"
          kicker="Systems / Products"
          title="Three systems. One forge."
          blurb="These aren't side projects — they're a stack. Crucible is the foundry where a fleet of AI agents builds and reviews software. Narsil, the reforged blade, is the deterministic-by-default pipeline they ship through. Carcin is what the line produces and runs in the field. Forge, deliver, operate — all on one security-first backbone."
        />

        <FlowStrip />

        <div className="mt-10 flex flex-col gap-5">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
